import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import WorkGraph from '../components/WorkGraph';
import '../styles/Hero.css';

// The rotation is the message: not defined by one thing.
const BUILD_WORDS = ['products', 'dev tools', 'web apps', 'systems', 'things people use'];

const TYPE_MS = 65;
const DELETE_MS = 40;
const HOLD_MS = 1300;
const GAP_MS = 300;

function useTypewriter(words: string[], reduce: boolean) {
  const [text, setText] = useState(reduce ? words[0] : '');
  const [phase, setPhase] = useState<'typing' | 'deleting'>('typing');
  const wordIndex = useRef(0);

  useEffect(() => {
    if (reduce) return;
    const word = words[wordIndex.current];
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === 'typing') {
      if (text.length < word.length) {
        timeout = setTimeout(() => setText(word.slice(0, text.length + 1)), TYPE_MS);
      } else {
        timeout = setTimeout(() => setPhase('deleting'), HOLD_MS);
      }
    } else {
      if (text.length > 0) {
        timeout = setTimeout(() => setText(word.slice(0, text.length - 1)), DELETE_MS);
      } else {
        wordIndex.current = (wordIndex.current + 1) % words.length;
        timeout = setTimeout(() => setPhase('typing'), GAP_MS);
      }
    }
    return () => clearTimeout(timeout);
  }, [text, phase, reduce, words]);

  return text;
}

const HeroPage: React.FC = () => {
  const reduce = !!useReducedMotion();
  const typed = useTypewriter(BUILD_WORDS, reduce);

  return (
    <div className="hero-section">
      <div className="hero-grid">

        {/* ── Left: thesis ── */}
        <div className="hero-copy">
          <motion.p
            className="font-display font-medium text-lg text-ink mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            Jaisheel Polimera
          </motion.p>

          <motion.h1
            className="hero-headline font-display font-bold text-ink"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            I build
            <br />
            <span className="hero-typed" aria-hidden="true">
              {typed}
              <span className={`hero-cursor${reduce ? ' hero-cursor--static' : ''}`} />
            </span>
            <span className="sr-only">products, dev tools, web apps, systems, and things people use.</span>
          </motion.h1>

          <motion.p
            className="hero-subline font-mono"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            Full-stack engineer, cloud architect, shipping real products.
          </motion.p>

          <motion.div
            className="hero-cta-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <a
              href="#Projects"
              className="hero-btn hero-btn--primary"
              onClick={e => {
                e.preventDefault();
                document.getElementById('Projects')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              View the work <span aria-hidden="true">→</span>
            </a>
            <a
              href="#Contact"
              className="hero-btn hero-btn--secondary"
              onClick={e => {
                e.preventDefault();
                document.getElementById('Contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Get in touch
            </a>
          </motion.div>
        </div>

        {/* ── Right: interactive work graph ── */}
        <motion.div
          className="hero-graph-slot"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
        >
          <WorkGraph />
        </motion.div>

      </div>
    </div>
  );
};

export default HeroPage;
